import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
  createKpiInputSchema,
  currencySchema,
  unitSchema,
  valueTypeSchema,
  type CreateKpiInput,
  type FormulaNodeDto,
  type FormulaWarningDto,
  type Pillar,
} from 'chery-contracts';
import { formatFormula, tokensToAst, type FormulaToken } from 'chery-kpi-engine';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { orpc } from '../../apis/orpc-query';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { DefinitionList } from '../../components/ui/DefinitionList';
import { Dialog } from '../../components/ui/Dialog';
import { FormField, FormSection } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Notice } from '../../components/ui/Notice';
import { Select, type SelectOption } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { Textarea } from '../../components/ui/Textarea';
import { clearableTextFieldOptions, errorData, isConflictError, nullableSelectOptions, serverMessage } from '../../lib/form-helpers';
import { DIVISION_META, divisionBelongsTo, PILLAR_META, PILLARS } from '../../lib/pillars';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { formulaOperands, formulaWarningsFrom, tokensFromFormula } from './formula-display';
import { FormulaBuilder } from './FormulaBuilder';
import { parentOptions, unitShortLabel, type KpiRow } from './kpi-display';

export type KpiSheetMode = 'create' | 'edit' | 'view';

export interface KpiSheetProps {
  isOpen: boolean;
  mode: KpiSheetMode;
  /** Present for `edit` and `view`. */
  kpi?: KpiRow;
  /** Every KPI the caller may see, for the parent choices. */
  allRows: readonly KpiRow[];
  onClose: () => void;
  onSaved: (name: string, created: boolean) => void;
}

type KpiForm = CreateKpiInput;

const EMPTY_VALUES: KpiForm = {
  pillar: 'GET',
  name: '',
  unit: 'NUMBER',
  valueType: 'ABSOLUTE',
  source: 'MANUAL',
  status: 'ACTIVE',
};

const toFormValues = (kpi: KpiRow): KpiForm => ({
  pillar: kpi.pillar,
  division: kpi.division,
  name: kpi.name,
  description: kpi.description,
  measurementMethod: kpi.measurementMethod,
  unit: kpi.unit,
  currency: kpi.currency,
  valueType: kpi.valueType,
  source: kpi.source,
  dataSource: kpi.dataSource,
  parentKpiId: kpi.parentKpiId,
  status: kpi.status,
});

/** What a save sends; kept while the "Save anyway" dialog asks to confirm formula warnings. */
type KpiPayload = Omit<KpiForm, 'code'> & { code?: string; formula?: FormulaNodeDto | null };

interface PendingConfirmation {
  payload: KpiPayload;
  warnings: FormulaWarningDto[];
}

/**
 * Create, edit and (for roles without write rights) view a KPI. The form validates against the contract's own
 * `createKpiInputSchema`, so the currency-iff-Currency rule is the service's rule. Choices that depend on the
 * pillar (division, parent) only offer what the service would accept; it re-checks everything anyway. A calculated
 * KPI is built in the `FormulaBuilder`; when the service answers 409 with formula warnings, a dialog lists them and
 * "Save anyway" repeats the save with `confirmWarnings`.
 */
export const KpiSheet = ({ isOpen, mode, kpi, allRows, onClose, onSaved }: KpiSheetProps) => {
  const { t } = useI18nTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [tokens, setTokens] = useState<FormulaToken[]>([]);
  const [pending, setPending] = useState<PendingConfirmation | null>(null);
  const [confirming, setConfirming] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<KpiForm>({ resolver: zodResolver(createKpiInputSchema), defaultValues: EMPTY_VALUES });

  useEffect(() => {
    if (isOpen) {
      setSubmitError(null);
      setPending(null);
      setTokens(kpi && mode !== 'create' ? tokensFromFormula(kpi.formula) : []);
      reset(kpi && mode !== 'create' ? toFormValues(kpi) : EMPTY_VALUES);
    }
  }, [isOpen, kpi, mode, reset]);

  const createKpi = useMutation(orpc.kpis.create.mutationOptions());
  const updateKpi = useMutation(orpc.kpis.update.mutationOptions());

  const pillar = watch('pillar') as Pillar;
  const unit = watch('unit');
  const currency = watch('currency') ?? null;
  const source = watch('source');
  const valueType = watch('valueType');
  const operands = useMemo(() => formulaOperands(allRows, mode === 'create' ? null : (kpi?.id ?? null)), [allRows, mode, kpi]);
  const formulaLocked = mode !== 'create' && kpi?.restricted === true;

  const pillarOptions: SelectOption[] = PILLARS.map((value) => ({ value, label: PILLAR_META[value].label }));
  const divisionOptions: SelectOption[] = PILLAR_META[pillar].divisions.map((value) => ({
    value,
    label: `${value} · ${DIVISION_META[value].label}`,
  }));
  const unitOptions: SelectOption[] = unitSchema.options.map((value) => ({ value, label: t(`enums.unit.${value}`) }));
  const currencyOptions: SelectOption[] = currencySchema.options.map((value) => ({ value, label: value }));
  const valueTypeOptions: SelectOption[] = valueTypeSchema.options.map((value) => ({ value, label: t(`enums.valueType.${value}`) }));
  const parents = useMemo(() => parentOptions(allRows, pillar, mode === 'create' ? null : (kpi?.id ?? null)), [allRows, pillar, mode, kpi]);
  const parentSelectOptions: SelectOption[] = parents.map((row) => ({ value: row.id, label: `${'— '.repeat(row.depth)}${row.name} (${row.code})` }));

  /** One save; a 409 carrying formula warnings opens the confirmation dialog instead of failing. */
  const send = async (payload: KpiPayload, confirmWarnings: boolean): Promise<void> => {
    try {
      if (mode === 'edit' && kpi) {
        const { code: _code, ...patch } = payload;
        const saved = await updateKpi.mutateAsync({
          id: kpi.id,
          ...patch,
          valueType: patch.valueType ?? kpi.valueType,
          status: patch.status ?? kpi.status,
          ...(confirmWarnings ? { confirmWarnings } : {}),
        });

        onSaved(saved.name, false);
      } else {
        const saved = await createKpi.mutateAsync({ ...payload, ...(confirmWarnings ? { confirmWarnings } : {}) });

        onSaved(saved.name, true);
      }

      setPending(null);
    } catch (error) {
      const warnings = isConflictError(error) ? formulaWarningsFrom(errorData(error)) : [];

      if (warnings.length > 0 && !confirmWarnings) {
        setPending({ payload, warnings });
      } else {
        setPending(null);
        setSubmitError(serverMessage(error) ?? t('errors.saveFailed'));
      }
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);

    // Choices made under another pillar are dropped rather than sent across pillars.
    const division = values.division && divisionBelongsTo(values.division, values.pillar) ? values.division : null;
    const parentKpiId = values.parentKpiId && parents.some((row) => row.id === values.parentKpiId) ? values.parentKpiId : null;
    const payload: KpiPayload = {
      ...values,
      division,
      parentKpiId,
      currency: values.unit === 'CURRENCY' ? (values.currency ?? null) : null,
    };

    // A restricted formula is never sent back: the author cannot see it, so it is left exactly as it is.
    if (values.source === 'CALCULATED' && !formulaLocked) {
      const built = tokensToAst(tokens);

      if (!built.ok) {
        setSubmitError(t('kpis.formula.fixFirst', { error: built.error }));

        return;
      }

      payload.formula = built.ast as FormulaNodeDto;
    }

    await send(payload, false);
  });

  if (mode === 'view' && kpi) {
    const parent = allRows.find((row) => row.id === kpi.parentKpiId);

    return (
      <Sheet
        isOpen={isOpen}
        onClose={onClose}
        title={t('kpis.form.viewTitle')}
        description={`${kpi.code} · ${PILLAR_META[kpi.pillar].label}`}
      >
        <DefinitionList
          items={[
            { label: t('kpis.form.name'), value: kpi.name },
            { label: t('kpis.form.code'), value: <span className="font-mono text-xs">{kpi.code}</span> },
            { label: t('kpis.form.description'), value: kpi.description ?? '—' },
            { label: t('kpis.form.measurementMethod'), value: kpi.measurementMethod ?? '—' },
            { label: t('kpis.form.pillar'), value: PILLAR_META[kpi.pillar].label },
            { label: t('kpis.form.division'), value: kpi.division ? `${kpi.division} · ${DIVISION_META[kpi.division].label}` : '—' },
            { label: t('kpis.form.parent'), value: parent ? parent.name : '—' },
            { label: t('kpis.form.unit'), value: `${t(`enums.unit.${kpi.unit}`)} (${unitShortLabel(kpi.unit, kpi.currency)})` },
            {
              label: t('kpis.form.valueType'),
              value: (
                <>
                  {t(`enums.valueType.${kpi.valueType}`)}
                  <span className="block text-xs text-muted-foreground">{t(`enums.valueTypeHint.${kpi.valueType}`)}</span>
                </>
              ),
            },
            { label: t('kpis.form.source'), value: t(`enums.source.${kpi.source}`) },
            ...(kpi.source === 'CALCULATED'
              ? [
                  {
                    label: t('kpis.form.formula'),
                    value: kpi.restricted ? (
                      t('kpis.formula.restricted')
                    ) : (
                      <span className="font-mono text-xs">
                        {kpi.formula
                          ? formatFormula(kpi.formula, (id) => allRows.find((row) => row.id === id)?.name ?? t('kpis.formula.unknownKpi'))
                          : '—'}
                      </span>
                    ),
                  },
                ]
              : []),
            { label: t('kpis.form.dataSource'), value: kpi.dataSource ?? '—' },
            {
              label: t('kpis.columns.status'),
              value: kpi.status === 'ACTIVE' ? t('enums.status.ACTIVE') : <Badge tone="outline">{t('enums.status.INACTIVE')}</Badge>,
            },
          ]}
        />
      </Sheet>
    );
  }

  const isEdit = mode === 'edit';

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('kpis.form.editTitle') : t('kpis.form.createTitle')}
      description={isEdit && kpi ? `${kpi.code} · ${t('kpis.form.editSubtitle')}` : t('kpis.form.createSubtitle')}
      className="max-w-lg"
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="kpi-form"
            isLoading={isSubmitting}
          >
            {isSubmitting ? t('actions.saving') : t('actions.save')}
          </Button>
        </>
      }
    >
      <form
        id="kpi-form"
        noValidate
        onSubmit={onSubmit}
        className="space-y-6"
      >
        <FormSection title={t('kpis.form.sectionDefinition')}>
          <FormField
            id="kpi-name"
            label={t('kpis.form.name')}
            error={errors.name?.message}
          >
            <Input
              id="kpi-name"
              hasError={Boolean(errors.name)}
              {...register('name')}
            />
          </FormField>

          {isEdit && kpi ? (
            <FormField
              id="kpi-code"
              label={t('kpis.form.code')}
              hint={t('kpis.form.codeReadOnly')}
            >
              <Input
                id="kpi-code"
                value={kpi.code}
                readOnly
                disabled
                className="font-mono"
              />
            </FormField>
          ) : (
            <FormField
              id="kpi-code"
              label={t('kpis.form.code')}
              error={errors.code?.message}
              hint={t('kpis.form.codeHint')}
            >
              <Input
                id="kpi-code"
                className="font-mono uppercase"
                hasError={Boolean(errors.code)}
                {...register('code', { setValueAs: (value: unknown) => (typeof value === 'string' && value.trim() !== '' ? value : undefined) })}
              />
            </FormField>
          )}

          <FormField
            id="kpi-description"
            label={t('kpis.form.description')}
            error={errors.description?.message}
          >
            <Textarea
              id="kpi-description"
              rows={3}
              hasError={Boolean(errors.description)}
              {...register('description', clearableTextFieldOptions)}
            />
          </FormField>

          <FormField
            id="kpi-data-source"
            label={t('kpis.form.dataSource')}
            error={errors.dataSource?.message}
          >
            <Input
              id="kpi-data-source"
              placeholder={t('kpis.form.dataSourcePlaceholder')}
              hasError={Boolean(errors.dataSource)}
              {...register('dataSource', clearableTextFieldOptions)}
            />
          </FormField>
        </FormSection>

        <FormSection title={t('kpis.form.sectionPlacement')}>
          <div className="grid grid-cols-2 gap-3">
            <FormField
              id="kpi-pillar"
              label={t('kpis.form.pillar')}
              error={errors.pillar?.message}
            >
              <Select
                id="kpi-pillar"
                options={pillarOptions}
                {...register('pillar')}
              />
            </FormField>
            <FormField
              id="kpi-division"
              label={t('kpis.form.division')}
              error={errors.division?.message}
            >
              <Select
                id="kpi-division"
                placeholder={t('kpis.form.noDivision')}
                options={divisionOptions}
                {...register('division', nullableSelectOptions)}
              />
            </FormField>
          </div>

          <FormField
            id="kpi-parent"
            label={t('kpis.form.parent')}
            error={errors.parentKpiId?.message}
            hint={t('kpis.form.parentHint')}
          >
            <Select
              id="kpi-parent"
              placeholder={t('kpis.form.noParent')}
              options={parentSelectOptions}
              {...register('parentKpiId', nullableSelectOptions)}
            />
          </FormField>
        </FormSection>

        <FormSection title={t('kpis.form.sectionMeasure')}>
          <div className="grid grid-cols-2 gap-3">
            <FormField
              id="kpi-unit"
              label={t('kpis.form.unit')}
              error={errors.unit?.message}
            >
              <Select
                id="kpi-unit"
                options={unitOptions}
                {...register('unit')}
              />
            </FormField>
            {unit === 'CURRENCY' ? (
              <FormField
                id="kpi-currency"
                label={t('kpis.form.currency')}
                error={errors.currency?.message}
              >
                <Select
                  id="kpi-currency"
                  placeholder={t('kpis.form.currencyPlaceholder')}
                  options={currencyOptions}
                  hasError={Boolean(errors.currency)}
                  {...register('currency', nullableSelectOptions)}
                />
              </FormField>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField
              id="kpi-value-type"
              label={t('kpis.form.valueType')}
              hint={t(`enums.valueTypeHint.${valueType}`)}
            >
              <Select
                id="kpi-value-type"
                options={valueTypeOptions}
                {...register('valueType')}
              />
            </FormField>
            <FormField
              id="kpi-measurement-method"
              label={t('kpis.form.measurementMethod')}
              error={errors.measurementMethod?.message}
            >
              <Input
                id="kpi-measurement-method"
                placeholder={t('kpis.form.measurementMethodPlaceholder')}
                {...register('measurementMethod', clearableTextFieldOptions)}
              />
            </FormField>
          </div>

          <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium text-card-foreground">{t('kpis.form.source')}</legend>
            <div className="flex items-center gap-5 text-sm">
              {(['MANUAL', 'CALCULATED'] as const).map((value) => (
                <label
                  key={value}
                  className="inline-flex items-center gap-2"
                >
                  <input
                    type="radio"
                    value={value}
                    data-kpi-source={value}
                    className="accent-primary"
                    disabled={formulaLocked}
                    {...register('source')}
                  />
                  {t(`enums.source.${value}`)}
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{t(source === 'CALCULATED' ? 'kpis.form.calculatedHint' : 'kpis.form.manualHint')}</p>
          </fieldset>

          {source === 'CALCULATED' ? (
            formulaLocked ? (
              <Notice>{t('kpis.formula.restricted')}</Notice>
            ) : (
              <FormulaBuilder
                tokens={tokens}
                onChange={setTokens}
                operands={operands}
                unit={unit}
                currency={unit === 'CURRENCY' ? currency : null}
              />
            )
          ) : null}

          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <Checkbox
                id="kpi-active"
                label={t('kpis.form.active')}
                checked={field.value !== 'INACTIVE'}
                onChange={(event) => field.onChange(event.target.checked ? 'ACTIVE' : 'INACTIVE')}
              />
            )}
          />
        </FormSection>

        {submitError ? <Notice tone="danger">{submitError}</Notice> : null}
      </form>

      <Dialog
        isOpen={pending !== null}
        onClose={() => setPending(null)}
        title={t('kpis.formula.confirmTitle')}
        description={t('kpis.formula.confirmBody')}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setPending(null)}
            >
              {t('kpis.formula.keepEditing')}
            </Button>
            <Button
              isLoading={confirming}
              onClick={async () => {
                if (pending) {
                  setConfirming(true);
                  await send(pending.payload, true);
                  setConfirming(false);
                }
              }}
            >
              {t('kpis.formula.saveAnyway')}
            </Button>
          </>
        }
      >
        <ul
          data-formula-warnings
          className="list-disc space-y-1.5 ps-5 text-sm text-foreground"
        >
          {(pending?.warnings ?? []).map((warning) => (
            <li key={warning.code + warning.message}>{warning.message}</li>
          ))}
        </ul>
      </Dialog>
    </Sheet>
  );
};
